export type { UserInfo } from "@modules/shared/models/user.model";

export interface MediaDto {
  key: string;
  url: string;
}

export interface BrandDto {
  id: string;
  name: string;
  description: string;
  owner_id: string;
  members: string[];
  logo: MediaDto | null;
  banner: MediaDto | null;
  media: MediaDto[];
  show_social: boolean;
  created_at: string;
  updated_at: string;
}

export interface ShopDto {
  id: string;
  name: string;
  location: string;
  b2b_id: string;
  image: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProductOptionDto {
  key: string;
  values: string[];
}

export interface ProductDescriptionDto {
  en: string;
  vi: string;
}

export interface ProductDetailInformationDto {
  warranty: number | null;
  warranty_unit: string | null;
  origin: string;
  send_from: string;
}

export type ProductStatus =
  | "TRACKER"
  | "UNLIMITED"
  | "PRE_ORDER"
  | "OUT_OF_STOCK";
export type PaymentMethod = "CONTACT" | "PAYOS";

export interface ProductItem {
  id: string;
  name: string;
  shop_id: string;
  category: string;
  price_unit: string;
  price: string;
  images: string[];
  options: ProductOptionDto[];
  description: ProductDescriptionDto;
  stock: number;
  bought: number;
  status: ProductStatus;
  detail_information: ProductDetailInformationDto;
  discount_percentage: number;
  payment_method: PaymentMethod;
  created_at: string;
  updated_at: string;
}

export type CreateProductInput = {
  name: string;
  shop_id: string;
  category: string;
  price_unit: string;
  price: string;
  existing_images?: string[];
  new_image_files?: File[];
  options: ProductOptionDto[];
  description: ProductDescriptionDto;
  stock: number;
  bought: number;
  status: ProductStatus;
  detail_information: ProductDetailInformationDto;
  discount_percentage: number;
  trade_discount_percentage: number;
  payment_method: PaymentMethod;
};

export type UpdateProductInput = Omit<CreateProductInput, "bought"> & {
  item_id: string;
};

// Mapping functions
export const mapMediaDto = (value: unknown): MediaDto | null => {
  if (!value) return null;
  if (typeof value === "string") {
    return value ? { key: "", url: value } : null;
  }
  if (typeof value !== "object") return null;
  const media = value as Record<string, unknown>;
  return {
    key: String(media.key ?? ""),
    url: String(media.url ?? ""),
  };
};

export const mapBrandDto = (value: unknown): BrandDto => {
  const data = (value ?? {}) as Record<string, unknown>;
  return {
    id: String(data.id ?? data._id ?? ""),
    name: String(data.name ?? ""),
    description: String(data.description ?? ""),
    owner_id: String(data.owner_id ?? data.ownerId ?? data.user_id ?? ""),
    members: Array.isArray(data.members)
      ? data.members.map(String).filter(Boolean)
      : [],
    logo: mapMediaDto(data.logo),
    banner: mapMediaDto(data.banner),
    media: Array.isArray(data.media)
      ? data.media.map(mapMediaDto).filter((m): m is MediaDto => m !== null)
      : [],
    show_social: Boolean(data.show_social ?? data.showSocial),
    created_at: String(data.created_at ?? data.createdAt ?? ""),
    updated_at: String(data.updated_at ?? data.updatedAt ?? ""),
  };
};

export const mapShopDto = (value: unknown): ShopDto => {
  const data = (value ?? {}) as Record<string, unknown>;
  return {
    id: String(data.id ?? data._id ?? ""),
    name: String(data.name ?? ""),
    location: String(data.location ?? ""),
    b2b_id: String(data.b2b_id ?? data.b2bId ?? ""),
    image: String(data.image ?? data.image_url ?? "") || null,
    created_at: String(data.created_at ?? data.createdAt ?? ""),
    updated_at: String(data.updated_at ?? data.updatedAt ?? ""),
  };
};

export const mapProductOptionDto = (
  value: unknown,
): ProductOptionDto | null => {
  if (!value || typeof value !== "object") return null;
  const data = value as Record<string, unknown>;
  return {
    key: String(data.key ?? ""),
    values: Array.isArray(data.values)
      ? data.values.map(String).filter(Boolean)
      : [],
  };
};

const toStringOrEmpty = (value: unknown) => {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return "";
};

const normalizeProductStatus = (value: unknown): ProductStatus => {
  const raw = toStringOrEmpty(value).toUpperCase();
  switch (raw) {
    case "IN_STOCK":
      return "TRACKER";
    case "DISCONTINUED":
      return "OUT_OF_STOCK";
    case "TRACKER":
    case "UNLIMITED":
    case "PRE_ORDER":
    case "OUT_OF_STOCK":
      return raw;
    default:
      return "OUT_OF_STOCK";
  }
};

export const mapProductItem = (value: unknown): ProductItem => {
  const data = (value ?? {}) as Record<string, unknown>;
  const description = (data.description ?? {}) as Record<string, unknown>;
  const detailInfo = (data.detail_information ??
    data.detailInformation ??
    {}) as Record<string, unknown>;
  return {
    id: String(data.id ?? data._id ?? ""),
    name: String(data.name ?? ""),
    shop_id: String(data.shop_id ?? data.shopId ?? ""),
    category: String(data.category ?? ""),
    price_unit: String(data.price_unit ?? data.priceUnit ?? ""),
    price: String(data.price ?? "0"),
    images: Array.isArray(data.images)
      ? data.images.map(String).filter(Boolean)
      : [],
    options: Array.isArray(data.options)
      ? data.options
          .map(mapProductOptionDto)
          .filter((o): o is ProductOptionDto => o !== null)
      : [],
    description: {
      en: String(description.en ?? ""),
      vi: String(description.vi ?? ""),
    },
    stock:
      typeof data.stock === "number" ? data.stock : Number(data.stock ?? 0),
    bought:
      typeof data.bought === "number" ? data.bought : Number(data.bought ?? 0),
    status: normalizeProductStatus(data.status),
    detail_information: {
      warranty:
        detailInfo.warranty != null ? Number(detailInfo.warranty) : null,
      warranty_unit:
        String(detailInfo.warranty_unit ?? detailInfo.warrantyUnit ?? "") ||
        null,
      origin: String(detailInfo.origin ?? ""),
      send_from: String(detailInfo.send_from ?? detailInfo.sendFrom ?? ""),
    },
    discount_percentage:
      typeof data.discount_percentage === "number"
        ? data.discount_percentage
        : Number(data.discount_percentage ?? 0),
    payment_method:
      (String(
        data.payment_method ?? data.paymentMethod ?? "CONTACT",
      ) as PaymentMethod) || "CONTACT",
    created_at: String(data.created_at ?? data.createdAt ?? ""),
    updated_at: String(data.updated_at ?? data.updatedAt ?? ""),
  };
};
